import { Injectable } from '@angular/core';

interface RgbColor {
  red: number;
  green: number;
  blue: number;
}

export interface GetGradientsParams {
  predictions: number[][]; // Predictions matrix
  max: number; // Max value of predictions
  min: number; // Min value of predictions
}

/**
 * Service responsible to create gradients between 2 colors.
 */
@Injectable({
  providedIn: 'root',
})
export class GradientService {
  private color1: RgbColor = { red: 243, green: 251, blue: 246 };
  private color2: RgbColor = { red: 179, green: 230, blue: 192 };

  /**
   * Calculates gradient matrix based on the given predictions.
   *
   * @param params Params needed to calculate gradients matrix based on predictions.
   * @returns Gradients matrix.
   */
  getGradients(params: GetGradientsParams) {
    const { predictions, min, max } = params;
    const newGradientMatrix: string[][] = [];
    const diff = max - min;

    for (const predictionsRow of predictions) {
      const gradientsRow: string[] = [];

      for (const prediction of predictionsRow) {
        /** Percentage (float 0-1) of the current prediction between the max and min */
        const perc = 1 - ((max - prediction) / diff);
        gradientsRow.push(this.getGradient(perc));
      }

      newGradientMatrix.push(gradientsRow);
    }

    return newGradientMatrix;
  }

  /**
   * Returns new color based on the given percentage
   *
   * @param transition Transition percent between color1 and color2. Must be between 0 and 1.
   * @param color1 First color in gradient.
   * @param color2 Second color in gradient.
   * @returns RGB color between color1 and color2 based on transition.
   */
  private getGradient(transition: number, color1 = this.color1, color2 = this.color2) {
    const diffRed = color2.red - color1.red;
    const diffGreen = color2.green - color1.green;
    const diffBlue = color2.blue - color1.blue;

    const gradient = {
      red: Math.floor(color1.red + (diffRed * transition)),
      green: Math.floor(color1.green + (diffGreen * transition)),
      blue: Math.floor(color1.blue + (diffBlue * transition)),
    };

    return `rgb(${gradient.red}, ${gradient.green}, ${gradient.blue})`;
  }
}
